import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LogOut, ChevronsUpDown, Building2, Search, HelpCircle, Menu, X, Download } from "lucide-react";
import { INSTALL_EVENT } from "@/components/InstallPrompt";
import { SteveFab } from "@/components/SteveFab";
import { GlobalSearch } from "@/components/GlobalSearch";
import { DeadlineBell } from "@/components/DeadlineBell";
import {
  SquaresFour, Buildings, ListChecks, FileText, Sparkle,
  Graph, CopySimple, GridFour, CalendarCheck, ClipboardText, Tag, BookOpen, UsersThree,
  Truck, ChartLineUp, WarningOctagon, Certificate, Coins, CaretDown, Plus,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const consultantNav = [
  { title: null, items: [
    { to: "/", label: "Cruscotto", icon: SquaresFour, end: true },
    { to: "/clienti", label: "Clienti", icon: Buildings },
    { to: "/documenti", label: "Documenti", icon: FileText },
    { to: "/schemi", label: "Schemi & Requisiti", icon: ListChecks },
  ] },
  { title: "Analisi AI", items: [
    { to: "/assistente", label: "Chiedi a Steve", icon: Sparkle },
    { to: "/mappa", label: "Mappa Collegamenti", icon: Graph },
    { to: "/ridondanze", label: "Ridondanze", icon: CopySimple },
    { to: "/nomenclatura", label: "Nomenclatura", icon: Tag },
    { to: "/fonti-normative", label: "Fonti Normative", icon: BookOpen },
  ] },
  { title: "Controllo", items: [
    { to: "/copertura", label: "Copertura Requisiti", icon: GridFour },
    { to: "/audit", label: "Audit", icon: ClipboardText },
    { to: "/incidenti", label: "Incidenti & Near-miss", icon: WarningOctagon },
    { to: "/scadenze", label: "Agenda Scadenze", icon: CalendarCheck },
  ] },
  { title: "Anagrafiche & SGI", items: [
    { to: "/dipendenti", label: "Dipendenti & Formazione", icon: UsersThree },
    { to: "/schede-formative", label: "Schede Attività Formativa", icon: Certificate },
    { to: "/fornitori", label: "Qualifica Fornitori", icon: Truck },
    { to: "/riesame", label: "Riesame Direzione", icon: ChartLineUp },
  ] },
];

const HELP_MAP_CONSULTANT = {
  "/": "s3-1", "/clienti": "s5-1", "/documenti": "s3-2", "/schemi": "s3-3",
  "/assistente": "cap4", "/mappa": "s3-5", "/ridondanze": "s3-6", "/nomenclatura": "s3-7",
  "/fonti-normative": "s3-8", "/copertura": "s3-9", "/audit": "s3-10", "/incidenti": "s3-11",
  "/scadenze": "s3-12", "/dipendenti": "s3-13", "/schede-formative": "s3-14",
  "/fornitori": "s3-15", "/riesame": "s3-16", "/consumi-ai": "s5-2",
};
const HELP_MAP_CLIENT = {
  "/": "s3-1", "/documenti": "s3-2", "/assistente": "cap4", "/mappa": "s3-4",
  "/copertura": "s3-5", "/audit": "s3-6", "/incidenti": "s3-7", "/scadenze": "s3-8",
  "/dipendenti": "s3-9", "/schede-formative": "s3-10", "/fornitori": "s3-11",
};

function resolveHelpAnchor(map, path) {
  if (map[path]) return map[path];
  const match = Object.keys(map)
    .filter((k) => k !== "/" && (path === k || path.startsWith(k + "/")))
    .sort((a, b) => b.length - a.length)[0];
  return match ? map[match] : "top";
}

const clientNav = [
  { title: null, items: [
    { to: "/", label: "Cruscotto", icon: SquaresFour, end: true },
    { to: "/documenti", label: "Documenti", icon: FileText },
    { to: "/copertura", label: "Conformità", icon: GridFour },
    { to: "/audit", label: "Audit", icon: ClipboardText },
    { to: "/incidenti", label: "Incidenti", icon: WarningOctagon },
    { to: "/dipendenti", label: "Dipendenti", icon: UsersThree },
    { to: "/schede-formative", label: "Schede Formative", icon: Certificate },
    { to: "/fornitori", label: "Fornitori", icon: Truck },
    { to: "/scadenze", label: "Agenda Scadenze", icon: CalendarCheck },
  ] },
  { title: "Analisi AI", items: [
    { to: "/assistente", label: "Chiedi a Steve", icon: Sparkle },
    { to: "/mappa", label: "Mappa Collegamenti", icon: Graph },
  ] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { tenantId, setTenantId, organizations, currentOrg } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const isConsultant = user?.role === "consultant";
  const guideHref = isConsultant ? "/manuali/manuale-consulente.html" : "/manuali/manuale-cliente.html";
  const helpHref = `${guideHref}#${resolveHelpAnchor(isConsultant ? HELP_MAP_CONSULTANT : HELP_MAP_CLIENT, location.pathname)}`;
  const baseSections = isConsultant ? consultantNav : clientNav;
  const sections = (isConsultant && user?.super_admin)
    ? [...baseSections, { title: "Amministrazione", items: [
        { to: "/consumi-ai", label: "Consumi AI / CBA", icon: Coins },
      ] }]
    : baseSections;
  const allItems = [];
  sections.forEach((s) => s.items.forEach((it) => allItems.push(it)));
  const itemByTo = Object.fromEntries(allItems.map((i) => [i.to, i]));
  const defaultFavTos = isConsultant
    ? ["/assistente", "/audit", "/documenti", "/copertura"]
    : ["/assistente", "/documenti", "/copertura"];
  const favKey = `synesis_favs_${user?.role || "u"}`;
  const [favTos, setFavTos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(favKey)); return Array.isArray(s) && s.length ? s : defaultFavTos; }
    catch { return defaultFavTos; }
  });
  const persistFavs = (arr) => { setFavTos(arr); localStorage.setItem(favKey, JSON.stringify(arr)); };
  const addFav = (to) => { if (!favTos.includes(to)) persistFavs([...favTos, to]); };
  const removeFav = (to) => persistFavs(favTos.filter((t) => t !== to));
  const favItems = favTos.map((to) => itemByTo[to]).filter(Boolean);
  const addable = allItems.filter((i) => !favTos.includes(i.to));
  const [collapsed, setCollapsed] = useState({});
  const toggleGroup = (t) => setCollapsed((c) => ({ ...c, [t]: !c[t] }));
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex text-slate-200 bg-[#070b16]">
      {/* Scrim mobile */}
      {navOpen && (
        <div onClick={() => setNavOpen(false)} data-testid="nav-scrim"
          className="lg:hidden fixed inset-0 z-40 bg-[#02060f]/70 backdrop-blur-[2px] animate-fade-up" />
      )}

      {/* Sidebar */}
      <aside data-testid="app-sidebar"
        className={`w-64 shrink-0 flex flex-col fixed h-screen z-50 overflow-hidden text-white border-r border-white/10 bg-gradient-to-b from-[#0A1734] via-[#0A1027] to-[#06090F] transition-transform duration-300 ease-out lg:translate-x-0 ${navOpen ? "translate-x-0 shadow-[30px_0_60px_-20px_rgba(0,0,0,0.85)]" : "-translate-x-full"}`}>
        <div className="pointer-events-none absolute -top-24 -left-16 h-64 w-64 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="pointer-events-none absolute top-1/3 -right-16 h-56 w-56 rounded-full bg-blue-600/15 blur-3xl" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-indigo-400/30 to-transparent" />
        <div className="pointer-events-none absolute left-0 right-0 h-32 bg-gradient-to-b from-transparent via-cyan-400/10 to-transparent blur-md animate-[sidebar-scan_7s_linear_infinite]" />

        <div className="relative h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
          <img src="/synesis-logo-mark-light.png" alt="Synesis AI" className="h-7 w-auto object-contain" />
          <button onClick={() => setNavOpen(false)} data-testid="nav-close-btn" aria-label="Chiudi menu"
            className="lg:hidden ml-auto h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative px-3 pt-4 pb-1">
          <div className="px-1 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-400/70 font-semibold">Preferiti</div>
          <div className="flex gap-2">
            {favItems.map((f) => {
              const active = location.pathname === f.to;
              return (
                <div key={f.to} className="relative flex-1 group/fav">
                  <NavLink to={f.to} title={f.label}
                    data-testid={`fav-${f.label.toLowerCase().replace(/\s|&/g, "-")}`}
                    className={`h-11 w-full rounded-xl flex items-center justify-center transition-all duration-200 ${active ? "bg-indigo-500/25 text-white ring-1 ring-inset ring-indigo-400/30 shadow-[0_0_18px_-6px_rgba(99,102,241,0.9)]" : "bg-white/[0.06] text-slate-300 hover:bg-white/[0.12] hover:text-white hover:-translate-y-0.5"}`}>
                    <f.icon size={18} weight={active ? "fill" : "regular"} />
                  </NavLink>
                  <button onClick={(e) => { e.preventDefault(); removeFav(f.to); }} title="Rimuovi dai preferiti"
                    data-testid={`fav-remove-${f.to.replace(/\//g, "")}`}
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-slate-700 text-slate-200 text-[11px] leading-none hidden group-hover/fav:flex items-center justify-center ring-1 ring-black/40 hover:bg-red-500 hover:text-white">×</button>
                </div>
              );
            })}
            {addable.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="fav-add-btn" title="Aggiungi ai preferiti"
                    className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center bg-white/[0.04] text-slate-400 ring-1 ring-inset ring-white/10 hover:bg-white/[0.1] hover:text-white transition-colors">
                    <Plus size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-auto">
                  <DropdownMenuLabel>Aggiungi ai preferiti</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {addable.map((it) => (
                    <DropdownMenuItem key={it.to} data-testid={`fav-add-${it.to.replace(/\//g, "")}`} onClick={() => addFav(it.to)}>
                      <it.icon size={16} className="mr-2 text-slate-500" /> {it.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <nav className="relative flex-1 px-3 pt-3 pb-5 space-y-2.5 overflow-y-auto">
          {sections.map((section, si) => {
            const isCollapsed = section.title ? !!collapsed[section.title] : false;
            return (
              <div key={si} className="rounded-xl bg-white/[0.035] border border-white/[0.07] p-1.5">
                {section.title && (
                  <button onClick={() => toggleGroup(section.title)}
                    data-testid={`nav-group-${section.title.toLowerCase().replace(/\s|&/g, "-")}`}
                    className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-400/80 font-semibold hover:text-slate-200 transition-colors">
                    <span>{section.title}</span>
                    <CaretDown size={12} className={`transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`} />
                  </button>
                )}
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s|&/g, "-")}`}
                        className={({ isActive }) =>
                          `group relative flex items-center gap-3 pl-4 pr-3 py-2 rounded-lg text-sm font-medium overflow-hidden transition-[background-color,color,transform,box-shadow,border-color] duration-200 ease-out will-change-transform ${
                            isActive
                              ? "bg-gradient-to-r from-indigo-500/25 via-blue-500/10 to-transparent text-white ring-1 ring-inset ring-indigo-400/25 shadow-[0_0_22px_-8px_rgba(99,102,241,0.85)]"
                              : "text-slate-300/80 hover:bg-white/[0.06] hover:text-white hover:translate-x-0.5"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full bg-gradient-to-b from-cyan-300 to-indigo-400 transition-all duration-300 ease-out ${isActive ? "opacity-100 shadow-[0_0_10px_1px_rgba(129,140,248,0.85)]" : "opacity-0 -translate-x-1.5"}`} />
                            {isActive && (
                              <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
                                <span className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent blur-[1px] animate-[scan_1.6s_ease-out_3]" />
                              </span>
                            )}
                            <item.icon size={18} weight={isActive ? "fill" : "regular"} className={`shrink-0 transition-transform duration-200 ease-out group-hover:scale-110 ${isActive ? "text-indigo-300 drop-shadow-[0_0_6px_rgba(129,140,248,0.7)]" : "text-slate-400 group-hover:text-white"}`} />
                            <span className="relative">{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="relative px-3 pb-1">
          <a href={guideHref} target="_blank" rel="noopener noreferrer" data-testid="nav-guida"
            className="group flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-lg text-sm font-medium text-slate-300/80 hover:bg-white/[0.06] hover:text-white hover:translate-x-0.5 transition-[background-color,color,transform] duration-200">
            <BookOpen size={18} className="shrink-0 text-slate-400 group-hover:text-white transition-transform duration-200 group-hover:scale-110" />
            <span>Guida</span>
          </a>
          <button onClick={() => window.dispatchEvent(new Event(INSTALL_EVENT))} data-testid="nav-installa-app"
            className="group w-full flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-lg text-sm font-medium text-slate-300/80 hover:bg-white/[0.06] hover:text-white hover:translate-x-0.5 transition-[background-color,color,transform] duration-200">
            <Download size={18} className="shrink-0 text-slate-400 group-hover:text-white transition-transform duration-200 group-hover:scale-110" />
            <span>Installa app</span>
          </button>
        </div>

        <div className="relative p-3 border-t border-white/10">
          <div className="flex items-center gap-2.5 rounded-lg bg-white/5 px-3 py-2.5 ring-1 ring-white/10">
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.5)]" />
            <div className="leading-tight">
              <div className="text-xs font-semibold text-white">{isConsultant ? "Consulente" : "Utente cliente"}</div>
              <div className="text-[10px] text-slate-400">Accesso attivo</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 w-full min-w-0 lg:ml-64 flex flex-col min-h-screen relative bg-[#070b16]">
        <div className="pointer-events-none fixed top-0 bottom-0 left-0 lg:left-64 right-0 z-0 overflow-hidden max-w-full">
          <div className="absolute -top-40 left-1/3 h-[32rem] w-[32rem] rounded-full bg-blue-600/25 blur-[130px] animate-float-slow" />
          <div className="absolute bottom-[-6rem] left-1/4 h-[30rem] w-[30rem] rounded-full bg-indigo-600/20 blur-[130px] animate-float" />
          <div className="absolute top-1/4 right-[-6rem] h-[26rem] w-[26rem] rounded-full bg-cyan-500/15 blur-[130px]" />
        </div>
        <header className="h-16 bg-[#0a1027]/70 backdrop-blur-xl border-b border-white/10 flex items-center justify-between gap-2 px-3 sm:px-5 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={() => setNavOpen(true)} data-testid="nav-open-btn" aria-label="Apri menu"
              className="lg:hidden h-10 w-10 shrink-0 rounded-xl flex items-center justify-center bg-white/[0.07] border border-white/10 text-slate-200 hover:bg-white/[0.14] hover:text-white transition-colors active:scale-95">
              <Menu className="h-5 w-5" />
            </button>
            {isConsultant ? (
              <>
                <span className="label-caps hidden xl:inline">Cliente attivo</span>
                <Select value={tenantId || "all"} onValueChange={(v) => setTenantId(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[9.5rem] sm:w-56 xl:w-64 h-9" data-testid="client-switcher">
                    <SelectValue placeholder="Tutti i clienti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i clienti</SelectItem>
                    {organizations.map((o) => (
                      <SelectItem key={o.id} value={o.id} data-testid={`switcher-org-${o.id}`}>
                        {o.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm font-medium min-w-0">
                <Building2 className="h-4 w-4 text-primary shrink-0" />
                <span className="truncate">{currentOrg?.nome || "La tua organizzazione"}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button onClick={() => setSearchOpen(true)} data-testid="open-search-mobile-btn" aria-label="Cerca"
              className="md:hidden flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-400 hover:text-primary transition-colors">
              <Search className="h-[18px] w-[18px]" />
            </button>
            <button onClick={() => setSearchOpen(true)} data-testid="open-search-btn"
              className="hidden md:flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-400 hover:border-primary/50 hover:text-slate-600 transition-colors text-sm">
              <Search className="h-4 w-4" /> Cerca...
              <kbd className="ml-1 text-[10px] font-mono bg-slate-100 rounded px-1.5 py-0.5 text-slate-500">⌘K</kbd>
            </button>
            <a href={helpHref} target="_blank" rel="noopener noreferrer" data-testid="help-context-btn"
              title="Guida di questa pagina"
              className="hidden sm:flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-400 hover:border-primary/50 hover:text-primary transition-colors">
              <HelpCircle className="h-[18px] w-[18px]" />
            </a>
            <DeadlineBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 hover:bg-slate-100 rounded-md px-2 py-1.5 transition-colors" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-white text-xs">
                    {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left leading-tight hidden sm:block">
                  <div className="text-sm font-medium">{user?.name || "Utente"}</div>
                  <div className="text-[11px] text-slate-400">{user?.email}</div>
                </div>
                <ChevronsUpDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="relative z-10 flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full min-w-0">
          <div key={location.pathname} className="animate-route">
            <Outlet />
          </div>
        </main>
        <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
        <SteveFab />
      </div>
    </div>
  );
}
